import AddIcon from "@mui/icons-material/Add";
import MenuIcon from "@mui/icons-material/Menu";
import {
  Button,
  ButtonBase,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
} from "@mui/material";
import AppBar from "@mui/material/AppBar";
import { styled } from "@mui/material/styles";
import {
  FC,
  lazy,
  Suspense,
} from "react";
import AccountDropdown from "../../components/AccountDropdown/AccountDropdown";
import Copyright from "../../components/Copyright";
import AgTipsModal from "../../components/dealerZone/AGTips/AgTipsModal";
import ChartAndNewsTab from "../../components/dealerZone/ChartNews/ChartAndNewsTab";
import LeadTimes from "../../components/dealerZone/LeadTimes/LeadTimes";
import LoseChangesModal from "../../components/dealerZone/Service/Modals/LoseChangesModal";
import ServiceRepair from "../../components/dealerZone/Service/ServiceRepair/ServiceRepair";
import StilesImgBanner from "../../components/dealerZone/StilesImgBanner";
import SwitchQuoteStatusTableModal from "../../components/dealerZone/Table/Modals/SwitchQuoteStatusTableModal";
import QuotesTable from "../../components/dealerZone/Table/QuotesTable/QuotesTable";
import TechSupportTooltip from "../../components/dealerZone/TechSupportTooltip";
import useDZoneSelector from "./useDZoneSelector";

const StyledRootDiv = styled("div")({
  display: "flex",
  position: "relative",
  minHeight: "100vh",
  minWidth: "fit-content",
  paddingBottom: "2.5rem",
  backgroundColor: "#fafafa",
});

const drawerWidth = 240;

const StyledAppbar = styled(AppBar)(({ theme }) => ({
  [theme.breakpoints.up("md")]: {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: drawerWidth,
  },
  height: 95,
  backgroundColor: "white",
  display: "flex",
  justifyContent: "space-evenly",
  alignItems: "center",
}));

const StyledToolbar = styled(Toolbar)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  width: "100%",
});

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  [theme.breakpoints.up("md")]: {
    display: "none",
  },
}));

const StyledFillerDiv = styled("div")(({ theme }) => ({
  width: 50,
  height: 50,
  backgroundColor: "white",
  [theme.breakpoints.down("md")]: {
    display: "none",
  },
}));

const StyledImgLogo = styled("img")(({ theme }) => ({
  height: 80,
  width: 300,
  [theme.breakpoints.down("lg")]: {
    width: 250,
  },
}));

const StyledNav = styled("nav")(({ theme }) => ({
  [theme.breakpoints.up("md")]: {
    width: drawerWidth,
    flexShrink: 0,
  },
}));

const StyledMain = styled("main")(({ theme }) => ({
  flexGrow: 1,
  margin: 20,
  padding: theme.spacing(3),
  height: "100%",
  width: "100%",
  [theme.breakpoints.down("lg")]: {
    margin: 0,
  },
}));

const StyledTypography = styled(Typography)(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    fontWeight: "bold",
    fontSize: 30,
    fontFamily: "Arial",
    paddingTop: 10,
  },
}));

const StyledNewQuoteDiv = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  width: "100%",
  margin: 20,
  [theme.breakpoints.down("lg")]: {
    justifyContent: "center",
    margin: 0,
  },
}));

const StyledNewQuoteButton = styled(Button)(({ theme }) => ({
  backgroundColor: "#4dae4f",
  flexBasis: "25%",
  color: "white",
  [theme.breakpoints.down("md")]: {
    flexBasis: "100%",
    height: "100%",
  },
}));

const StyledHomeDiv = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  [theme.breakpoints.down("xl")]: {
    width: "100%",
  },
}));

const StyledTopHomeDiv = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-around",
  alignItems: "flex-start",
  width: "100%",
  [theme.breakpoints.down("xl")]: {
    flexDirection: "column",
    alignItems: "center",
    paddingTop: 20,
  },
}));

const StyledQuotesDiv = styled("div")({
  display: "flex",
  height: 50,
  marginTop: 20,
  justifyContent: "space-between",
  position: "relative",
});

const StyledImgLogoDiv = styled("div")({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: 95,
});

const StyledDealerImg = styled("img")({
  maxWidth: 150,
  width: "auto",
  height: "auto",
  maxHeight: 80,
});

const CreateAccount = lazy(
  () => import("../../components/dealerZone/CreateAccount/CreateAccount")
);

const CustomLabels = lazy(
  () => import("../../components/dealerZone/CustomLabels/CustomLabels")
);

const AdminCalendar = lazy(
  () =>
    import("../../components/dealerZone/Calendar/AdminCalendar/AdminCalendar")
);

const SalesLog = lazy(
  () => import("../../components/dealerZone/SalesReport/SalesLog/SalesLog")
);

const GlassDetailsTable = lazy(
  () =>
    import("../../components/dealerZone/GlassDetails/Table/GlassDetailsTable")
);

const CustomerListTable = lazy(
  () => import("../../components/dealerZone/CustomerList/Table/CustomerListTable")
);

const InsightsTab = lazy(
  () => import("../../components/dealerZone/Insights/Tab/InsightsTab")
);

const CutsheetEdits = lazy(
  () => import("../../components/dealerZone/Cutsheet/CutsheetEdits")
);

export interface TableState {
  page: number;
  rowsPerPage: number;
  totalResults: number;
  searchTerm: string;
  filterBy: string;
  showLeadsOnly: boolean;
}

interface DealerZoneProps {
  window?: (() => { document: { body: Element } }) | undefined;
}

export type RepairState = { status: string; id: string };

const DealerZone: FC<DealerZoneProps> = ({ window }) => {
  const {
    tabOptions,
    findColor,
    h2Titles,
    handleCloseTips,
    openTips,
    handleClose,
    open,
    getSN,
    handleNewQuote,
    handleDrawerToggle,
    changeTab,
    handleTabChange,
    isLoading,
    error,
    data,
    isFetching,
    isLoadingQuotes,
    quotesError,
    quotesData,
    isFetchingQuotes,
    handleArchive,
    showArchive,
    setReSearchO,
    setReSearchQ,
    setIsSearchO,
    isSearchOrders,
    setIsSearchQ,
    isSearchQuotes,
    ordersTable,
    quotesTable,
    setQuotesTable,
    setOrdersTable,
    showTab,
    setShowTab,
    mobileOpen,
    mediaLg,
    theme,
    id,
    logo,
    role,
    snNumber,
    setSnNumber,
    repair,
    setRepair,
    openTableSwitchModal,
    handleTableSwitch,
    handleOpenTableSwitchModal,
    handleCloseSwitchTableModal,
  } = useDZoneSelector();

  const drawer = (
    <div style={{ minHeight: "100vh", paddingBottom: "2.5rem" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {!logo ||
        logo === "No Logo Found" ||
        role?.includes("Sales") ||
        role === "Admin" ? (
          <StyledImgLogoDiv>
            <Typography style={{ color: "black" }} variant="h5" noWrap>
              AG DEALER ZONE
            </Typography>
          </StyledImgLogoDiv>
        ) : (
          <StyledImgLogoDiv>
            <StyledDealerImg src={logo} alt="" />
          </StyledImgLogoDiv>
        )}
      </div>
      <Divider />
      <List>
        {tabOptions.map((tab) => {
          const { title, IconComponent } = tab;

          return (
            <ListItem
              component={ButtonBase}
              style={{ backgroundColor: findColor(title, "#4b4b4b") }}
              onClick={() => handleTabChange(title)}
              key={title}
            >
              <ListItemIcon>
                <IconComponent style={{ color: findColor(title, "white") }} />
              </ListItemIcon>
              <ListItemText
                style={{ color: findColor(title, "white") }}
                primary={title}
              />
            </ListItem>
          );
        })}
      </List>

      <Copyright position="center1" />
    </div>
  );

  const container =
    window !== undefined ? () => window().document.body : undefined;

  return (
    <StyledRootDiv>
      {role !== "Production" && (
        <AgTipsModal open={openTips} handleClose={handleCloseTips} />
      )}

      {showTab === "Home" && role !== "Production" && <TechSupportTooltip />}

      <SwitchQuoteStatusTableModal
        open={openTableSwitchModal}
        handleClose={handleCloseSwitchTableModal}
        statusTableLocation={showTab === "Orders" ? "Quotes" : "Orders"}
        switchTable={handleTableSwitch}
      />

      <StyledAppbar position="fixed">
        <StyledToolbar>
          <StyledIconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            size="large"
          >
            <MenuIcon style={{ color: "black" }} />
          </StyledIconButton>

          <StyledFillerDiv />

          <StyledImgLogo
            src="https://res.cloudinary.com/ag-millworks/image/upload/AG_Logos/AGlogoBlack.png"
            alt="logo"
          />

          <AccountDropdown
            color="#4b4b4b"
            resumeLaterBtn={false}
            returnedPath="dealerZone"
          />
        </StyledToolbar>
      </StyledAppbar>

      <StyledNav aria-label="dealerZone tabs">
        {!mediaLg ? (
          <Drawer
            container={container}
            variant="temporary"
            anchor={theme.direction === "rtl" ? "right" : "left"}
            open={mobileOpen}
            onClose={handleDrawerToggle}
            sx={{
              width: drawerWidth,
              flexShrink: 0,
              "& .MuiDrawer-paper": { width: drawerWidth },
            }}
            ModalProps={{
              keepMounted: true, // Better open performance on mobile.
            }}
          >
            {drawer}
          </Drawer>
        ) : (
          <Drawer
            sx={{
              width: drawerWidth,
              flexShrink: 0,
              "& .MuiDrawer-paper": { width: drawerWidth },
            }}
            variant="permanent"
            open
          >
            {drawer}
          </Drawer>
        )}
      </StyledNav>

      {role !== "Production" ? (
        <StyledMain>
          {showTab === "Cutsheets" ? null : (
            <Toolbar style={{ position: "relative" }} />
          )}

          <div
            style={
              showTab === "Quotes" ||
              showTab === "Orders" ||
              showTab === "Glass Details" ||
              showTab === "Customer List" ||
              showTab === "Cutsheets"
                ? {
                    display: "flex",
                    height: 50,
                    marginTop: 20,
                    justifyContent: "space-between",
                    position: "relative",
                  }
                : {
                    display: "flex",
                    height: 50,
                    marginTop: 20,
                    justifyContent: "flex-end",
                    position: "relative",
                    [theme.breakpoints.down("xl")]: {
                      width: "100%",
                    },
                  }
            }
          >
            {h2Titles.includes(showTab) && (
              <div style={{ flexBasis: "70%" }}>
                <StyledTypography variant="h2">{showTab}</StyledTypography>
              </div>
            )}

            {(showTab === "Quotes" ||
              showTab === "Orders" ||
              showTab === "Home") && (
              <StyledNewQuoteDiv>
                <StyledNewQuoteButton
                  onClick={handleNewQuote}
                  size="large"
                  variant="contained"
                  color="success"
                >
                  <AddIcon /> New Quote
                </StyledNewQuoteButton>
              </StyledNewQuoteDiv>
            )}
          </div>
          {showTab === "Quotes" && (
            <QuotesTable
              isSearch={isSearchQuotes}
              setIsSearch={setIsSearchQ}
              totalLength={quotesTable.totalResults}
              page={quotesTable.page}
              rowsPerPage={quotesTable.rowsPerPage}
              isFetching={isFetchingQuotes}
              data={quotesData}
              isLoading={isLoadingQuotes}
              error={quotesError}
              showTab={showTab}
              userId={id}
              filterBy={quotesTable.filterBy}
              searchTerm={quotesTable.searchTerm}
              setState={setQuotesTable}
              setReSearch={setReSearchQ}
              showArchive={showArchive}
              handleArchive={handleArchive}         />
          )}
          {showTab === "Orders" && (
            <QuotesTable
              isSearch={isSearchOrders}
              setIsSearch={setIsSearchO}
              totalLength={ordersTable.totalResults}
              page={ordersTable.page}
              rowsPerPage={ordersTable.rowsPerPage}
              isFetching={isFetching}
              data={data}
              isLoading={isLoading}
              error={error}
              showTab={showTab}
              userId={id}
              filterBy={ordersTable.filterBy}
              searchTerm={ordersTable.searchTerm}
              setState={setOrdersTable}
              setReSearch={setReSearchO}
            />
          )}

          {showTab === "Home" && (
            <StyledHomeDiv>
              <StyledTopHomeDiv>
                <LeadTimes />

                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    marginLeft: 10,
                  }}
                >
                  <ChartAndNewsTab />
                </div>
              </StyledTopHomeDiv>
              <StilesImgBanner />
            </StyledHomeDiv>
          )}

          {showTab === "Glass Details" && (
            <Suspense fallback={<div>Loading...</div>}>
              <GlassDetailsTable />
            </Suspense>
          )}

          {showTab === "Customer List" && (
            <Suspense fallback={<div>Loading...</div>}>
              <CustomerListTable setShowTab={setShowTab} />
            </Suspense>
          )}

          {showTab === "Service Repair" && (
            <>
              <LoseChangesModal
                handleClose={handleClose}
                open={open}
                changeTab={changeTab}
                modalText="Continuing Will Cause You to Lose Any Changes."
              />

              <ServiceRepair
                id={id}
                snNumber={snNumber}
                setSnNumber={setSnNumber}
                getSN={getSN}
                repair={repair}
                setRepair={setRepair}
              />
            </>
          )}

          {(role === "Admin" ||
            role === "Inside Sales" ||
            role === "Outside Sales") &&
            showTab === "Calendar" && (
              <Suspense fallback={<div>Loading...</div>}>
                <AdminCalendar role={role} />
              </Suspense>
            )}

          {role === "Admin" && showTab === "Create Account" && (
            <Suspense fallback={<div>Loading...</div>}>
              <CreateAccount />
            </Suspense>
          )}

          {role === "Admin" && showTab === "Cutsheets" && (
            <Suspense fallback={<div>Loading...</div>}>
              <CutsheetEdits />
            </Suspense>
          )}

          {role === "Admin" && showTab === "Custom Labels" && (
            <Suspense fallback={<div>Loading...</div>}>
              <CustomLabels />
            </Suspense>
          )}

          {showTab === "Sales Report" && (
            <Suspense fallback={<div>Loading...</div>}>
              <SalesLog role={role} />
            </Suspense>
          )}

          {showTab === "Insights" && (
            <Suspense fallback={<div>Loading...</div>}>
              <InsightsTab userId={id} />
            </Suspense>
          )}
        </StyledMain>
      ) : (
        <StyledMain>
          <Toolbar style={{ position: "relative" }} />

          {showTab === "Orders" ? (
            <>
              <StyledQuotesDiv>
                <div>
                  <StyledTypography variant="h2">Orders</StyledTypography>
                </div>
              </StyledQuotesDiv>
              <QuotesTable
                isSearch={isSearchOrders}
                setIsSearch={setIsSearchO}
                totalLength={ordersTable.totalResults}
                page={ordersTable.page}
                rowsPerPage={ordersTable.rowsPerPage}
                isFetching={isFetching}
                data={data}
                isLoading={isLoading}
                error={error}
                showTab={"Orders"}
                userId={id}
                filterBy={ordersTable.filterBy}
                searchTerm={ordersTable.searchTerm}
                 showLeadsOnly={quotesTable.showLeadsOnly}
                setState={setOrdersTable}
                setReSearch={setReSearchO}
              />
            </>
          ) : showTab === "Custom Labels" ? (
            <Suspense fallback={<div>Loading...</div>}>
              <CustomLabels />
            </Suspense>
          ) : showTab === "Glass Details" ? (
            <>
              <div style={{ flexBasis: "70%" }}>
                <StyledTypography variant="h2">Glass Details</StyledTypography>
              </div>

              <Suspense fallback={<div>Loading...</div>}>
                <GlassDetailsTable />
              </Suspense>
            </>
          ) : null}
        </StyledMain>
      )}
    </StyledRootDiv>
  );
};

export default DealerZone;
